import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';
import ProviderPaymentMethod from '@/shared/types/shipping/providerPaymentMethod.type';

import useProfileDetails from './hooks/useProfileDetails';

type ProfileDetailsProps = {
  profileDetailsState: ReturnType<typeof useProfileDetails>;
  templateSchemas: ProfilesTemplateSchemas;
  providerPaymentMethods: ProviderPaymentMethod[] | null;
};

const ProfileDetails: FC<ProfileDetailsProps> = ({
  profileDetailsState,
  templateSchemas,
  providerPaymentMethods,
}) => {
  const { t } = useTranslation(['shipping', 'forms']);

  const {
    profileDetailsForm,
    packagingProperties,
    formErrors,
    setProfileDetailsForm,
    setPackagingProperties,
  } = profileDetailsState;

  return (
    <Stack py={2} spacing={2}>
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Text,
            field_props: {
              required: true,
              name: 'display_name',
              label: t('forms:display_name'),
              placeholder: t('forms:display_name'),
              value: profileDetailsForm.display_name,
              error: !!formErrors?.display_name,
              helperText: formErrors?.display_name,
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'shipping_method_id',
              label: t('forms:shipping_method_id'),
              placeholder: t('forms:shipping_method_id'),
              value: profileDetailsForm.shipping_method_id,
              options: templateSchemas.methods.map((item) => ({
                label: `${item.carrier_name} - ${item.service_name}`,
                value: item.shipping_method_id,
              })),
              error: !!formErrors.shipping_method_id,
              helperText: formErrors.shipping_method_id,
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              name: 'package_type',
              label: t('forms:package_type'),
              placeholder: t('forms:package_type'),
              value: profileDetailsForm.package_type,
              options: templateSchemas.package_types.map((item) => {
                const label: Record<string, string> = {
                  'courier-pak': 'Courier Pack',
                  'envelope': 'Envelope',
                  'package': 'Package',
                  'pallet': 'Pallet',
                };

                return {
                  label: label[item.package_type],
                  value: item.package_type,
                };
              }),
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              name: 'payment_method_id',
              label: t('forms:payment_method_id'),
              placeholder: t('forms:payment_method_id'),
              value: profileDetailsForm.payment_method_id,
              disabled:
                !providerPaymentMethods || providerPaymentMethods.length === 0,
              options:
                providerPaymentMethods?.map((item) => ({
                  label: item.display_name,
                  value: item.payment_method_id,
                })) ?? [],
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'signature_requirement',
              label: t('forms:signature_requirement'),
              placeholder: t('forms:signature_requirement'),
              value: profileDetailsForm.signature_requirement,
              options: templateSchemas.signature_options.map((item) => {
                const label: Record<string, string> = {
                  'required': 'Required',
                  'not-required': 'Not Required',
                  'adult-required': 'Adult Required',
                };

                return {
                  label: label[item.signature_requirement],
                  value: item.signature_requirement,
                };
              }),
              error: !!formErrors.signature_requirement,
              helperText: formErrors.signature_requirement,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'category',
              label: t('forms:category'),
              placeholder: t('forms:category'),
              value: profileDetailsForm.category,
            },
          },
        ]}
        getFieldsValues={(value) => {
          setProfileDetailsForm((prev) => ({ ...prev, ...value }));
        }}
      />

      <WaveUIForm_V2
        fields={[
          {
            columns: 6,
            field_type: FieldType.Switch,
            field_props: {
              name: 'dangerous_goods',
              label: t('forms:dangerous_goods'),
              checked: packagingProperties.dangerous_goods,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Switch,
            field_props: {
              name: 'includes_return_label',
              label: t('forms:includes_return_label'),
              checked: packagingProperties.includes_return_label,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Switch,
            field_props: {
              name: 'enabled',
              label: t('forms:enabled'),
              checked: packagingProperties.enabled,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Switch,
            field_props: {
              name: 'internal_use_only',
              label: t('forms:internal_use_only'),
              checked: packagingProperties.internal_use_only,
            },
          },
        ]}
        getFieldsValues={(value) => {
          setPackagingProperties((prev) => ({ ...prev, ...value }));
        }}
      />
    </Stack>
  );
};

export default ProfileDetails;
